import { OnChanges, Component, Input, OnInit, Output, SimpleChanges, OnDestroy, ViewChild, ElementRef, EventEmitter, AfterViewInit } from '@angular/core';
import { ApexOptions } from 'apexcharts';
import { Currency } from 'src/app/models/currency';
import { CryptoPriceData, PriceChartDuration } from 'src/app/models/price-chart';
import { CryptoPriceService } from 'src/app/services/crypto-price.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'crypto-list-item',
  templateUrl: './crypto-list-item.component.html',
  styleUrls: ['./crypto-list-item.component.scss'],
  providers: [CryptoPriceService]
})
export class CryptoListItemComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @Input() currency: Currency;
  @Input() baseCurrencyCode: string = '';
  @Input() duration: PriceChartDuration;
  @Input() showCharts = false;
  @Input() cardMode = false;
  @Input() isFavorite = false;

  @Output() update = new EventEmitter<CryptoPriceData>();
  @Output() favoriteToggle = new EventEmitter<string>();

  @ViewChild('chartElm')
  chartElementRef: ElementRef;

  @ViewChild('mobileChartElm')
  mobileChartElementRef: ElementRef;

  @ViewChild('cardChartElm')
  cardChartElementRef: ElementRef;

  loading = true;

  data: CryptoPriceData;
  pair: string = '';
  chart: any;
  mobileChart: any;
  cardChart: any;

  useGlobalCurrency = !!environment.currencies.globalCurrency;
  dataLoaded = false;
  priceFlashClass = '';

  private _previousPrice: number = null;
  private _initialized = false; // true once data fetch has been triggered
  private _observer: IntersectionObserver;
  private _hostEl: ElementRef;

  constructor(
    private cryptoPriceService: CryptoPriceService,
    private elRef: ElementRef
  ) {
    this._hostEl = elRef;
  }

  ngOnInit(): void {
    if (this.currency?.code && this.baseCurrencyCode) {
      this.pair = `${this.currency.code}-${this.baseCurrencyCode}`;
    }

    // Subscribe to data changes (subscription is cheap, no fetch happens yet)
    this.cryptoPriceService.valueChanges.subscribe(val => {
      this.loading = false;

      // Price flash detection
      const newPrice = val?.baseCurrency?.lastPrice;
      if (this._previousPrice !== null && newPrice != null && newPrice !== this._previousPrice) {
        this.priceFlashClass = newPrice > this._previousPrice ? 'flash-green' : 'flash-red';
        setTimeout(() => { this.priceFlashClass = ''; }, 700);
      }
      if (newPrice != null) {
        this._previousPrice = newPrice;
      }

      this.data = val;
      this.pair = this.cryptoPriceService.pair;

      if (!this.dataLoaded && val) {
        this.dataLoaded = true;
      }

      this.renderChart();

      this.update.next(this.data);
    });
  }

  ngAfterViewInit(): void {
    // Lazy load: only trigger the initial fetch when the element scrolls into view
    this._observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !this._initialized) {
          this._startFetching();
          this._observer.disconnect();
        }
      },
      { rootMargin: '200px' } // start loading 200px before visible
    );
    this._observer.observe(this._hostEl.nativeElement);
  }

  /** Triggers the initial data fetch — called once when element becomes visible */
  private _startFetching() {
    this._initialized = true;
    this.cryptoPriceService.setBaseCurrency(this.baseCurrencyCode);
    this.cryptoPriceService.setCurrency(this.currency?.code);
    this.cryptoPriceService.setDuration(this.duration);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.currency || changes?.baseCurrencyCode) {
      const code = this.currency?.code;
      const base = this.baseCurrencyCode;
      if (code && base) {
        this.pair = `${code}-${base}`;
      }
    }

    // Only propagate changes to the service if we've already initialized.
    // Before initialization, the values are stored in inputs and will be
    // picked up by _startFetching() when the element becomes visible.
    if (!this._initialized) return;

    if (changes?.currency?.currentValue && !changes?.currency.firstChange && changes?.currency?.currentValue !== changes?.currency?.previousValue) {
      this.cryptoPriceService.setCurrency(changes?.currency?.currentValue?.code);
    }

    if (changes?.baseCurrencyCode?.currentValue && !changes?.baseCurrencyCode.firstChange && changes?.baseCurrencyCode?.currentValue !== changes?.baseCurrencyCode?.previousValue) {
      this.cryptoPriceService.setBaseCurrency(changes?.baseCurrencyCode?.currentValue);
    }

    if (changes?.duration?.currentValue && !changes?.duration.firstChange && changes?.duration?.currentValue !== changes?.duration?.previousValue) {
      this.cryptoPriceService.setDuration(changes?.duration?.currentValue);
    }

    if (changes?.cardMode && !changes.cardMode.firstChange && this.data) {
      if (changes.cardMode.currentValue) {
        // Switching to card: destroy table/mobile charts, render card after DOM update
        this.chart?.destroy();
        this.chart = null;
        this.mobileChart?.destroy();
        this.mobileChart = null;
        if (this.showCharts) {
          setTimeout(() => this.renderCardChart(), 0);
        }
      } else {
        // Switching to table: destroy card chart, render table chart after DOM update
        this.cardChart?.destroy();
        this.cardChart = null;
        setTimeout(() => this.renderChart(), 0);
      }
    }

    if (changes?.showCharts) {
      if (changes.showCharts.currentValue && this.data) {
        setTimeout(() => this.renderMobileChart(), 0);
        setTimeout(() => this.renderCardChart(), 0);
      } else if (!changes.showCharts.currentValue) {
        this.mobileChart?.destroy();
        this.mobileChart = null;
        this.cardChart?.destroy();
        this.cardChart = null;
      }
    }
  }

  ngOnDestroy(): void {
    this._observer?.disconnect();
    this.chart?.destroy();
    this.mobileChart?.destroy();
    this.cardChart?.destroy();
  }

  renderChart() {
    const element = this?.chartElementRef?.nativeElement;

    if (element) {
      if (!this.chart) {
        this.chart = new ApexCharts(element, this.getChartOptions(38));
        this.chart.render();
      } else {
        this.updateChart();
      }
    }

    if (this.showCharts) {
      this.renderMobileChart();
      this.renderCardChart();
    }
  }

  renderCardChart() {
    const cardEl = this?.cardChartElementRef?.nativeElement;
    if (!cardEl) return;

    const chartData = this.data?.priceHistory?.baseCurrencyData?.data;
    if (!chartData?.length) return;

    if (!this.cardChart) {
      this.cardChart = new ApexCharts(cardEl, this.getCardChartOptions());
      this.cardChart.render();
    } else {
      this.cardChart.updateOptions({
        series: [{ data: chartData }],
        colors: [this.data?.chartColor],
        yaxis: this.getCardYAxis(chartData),
      });
    }
  }

  renderMobileChart() {
    const mobileEl = this?.mobileChartElementRef?.nativeElement;
    if (!mobileEl) return;

    if (!this.mobileChart) {
      this.mobileChart = new ApexCharts(mobileEl, this.getChartOptions(60));
      this.mobileChart.render();
    } else {
      this.mobileChart.updateOptions({
        series: [{ data: this.data?.priceHistory?.baseCurrencyData?.data }],
        colors: [this.data?.chartColor],
      });
    }
  }

  updateChart() {
    const opts = {
      series: [{ data: this.data?.priceHistory?.baseCurrencyData?.data }],
      colors: [this.data?.chartColor],
    };
    this.chart?.updateOptions(opts);
    this.mobileChart?.updateOptions(opts);
  }

  onToggleFavorite(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.favoriteToggle.emit(this.currency?.code);
  }

  get durationLabel(): string {
    switch (this.duration) {
      case PriceChartDuration.OneDay: return '24H';
      case PriceChartDuration.SevenDays: return '7D';
      case PriceChartDuration.OneMonth: return '1M';
      case PriceChartDuration.ThreeMonths: return '3M';
      case PriceChartDuration.OneYear: return '1Y';
      default: return '24H';
    }
  }

  /** Ticker lastPrice, falling back to the newest price-history data point */
  get currentPrice(): number | null {
    if (this.data?.baseCurrency?.lastPrice) return this.data.baseCurrency.lastPrice;
    const chartData = this.data?.priceHistory?.baseCurrencyData?.data;
    if (chartData?.length) {
      const point: any = chartData[0];
      return typeof point === 'object' && point !== null ? point.y : point;
    }
    return null;
  }

  get priceRangePercent(): string {
    const low = this.periodLow;
    const high = this.periodHigh;
    const price = this.currentPrice;
    if (low == null || high == null || price == null || high === low) return '0%';
    const pct = Math.max(0, Math.min(100, ((price - low) / (high - low)) * 100));
    return `${pct}%`;
  }

  get periodHigh(): number | null {
    const data = this.data?.priceHistory?.baseCurrencyData?.data;
    if (data?.length) return this.extractYAxisRange(data).max;
    // Fallback to ticker dailyHigh while price history loads
    return this.data?.baseCurrency?.dailyHigh ?? null;
  }

  get periodLow(): number | null {
    const data = this.data?.priceHistory?.baseCurrencyData?.data;
    if (data?.length) return this.extractYAxisRange(data).min;
    // Fallback to ticker dailyLow while price history loads
    return this.data?.baseCurrency?.dailyLow ?? null;
  }

  private extractYAxisRange(chartData: any[]): { min: number; max: number } {
    if (!chartData?.length) return { min: 0, max: 1 };
    const prices = chartData.map((d: any) => (typeof d === 'object' && d !== null) ? d.y : d).filter(v => v != null && !isNaN(v));
    if (!prices.length) return { min: 0, max: 1 };
    return { min: Math.min(...prices), max: Math.max(...prices) };
  }

  private getCardYAxis(chartData: any[]) {
    const { min, max } = this.extractYAxisRange(chartData);
    // Determine decimal places based on price magnitude
    const decimals = max >= 1000 ? 0 : max >= 1 ? 2 : max >= 0.01 ? 4 : 6;
    return {
      show: true,
      min,
      max,
      tickAmount: 5,
      labels: {
        show: true,
        formatter: (val: number) => val?.toLocaleString(undefined, {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        }),
        style: { colors: ['rgba(255,255,255,0.3)'], fontSize: '10px' },
        offsetX: -5,
      },
    };
  }

  getCardChartOptions(): ApexOptions {
    const chartData = this.data?.priceHistory?.baseCurrencyData?.data || [];

    return {
      chart: {
        redrawOnWindowResize: true,
        parentHeightOffset: 0,
        height: 130,
        type: 'area',
        sparkline: { enabled: false },
        toolbar: { show: false },
        animations: { enabled: false },
      },
      grid: {
        show: false,
        padding: { left: 0, right: 0, top: -20, bottom: -10 },
      },
      dataLabels: { enabled: false },
      series: [{ data: chartData }],
      colors: [this.data?.chartColor || '#17C1A4'],
      stroke: { curve: 'smooth', width: 2 },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.3,
          opacityTo: 0.02,
          stops: [0, 100],
        },
      },
      tooltip: { enabled: false },
      xaxis: {
        labels: { show: false },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: this.getCardYAxis(chartData),
    };
  }

  getChartOptions(height: number): ApexOptions {
    return {
      chart: {
        redrawOnWindowResize: false,
        parentHeightOffset: 0,
        height,
        type: 'line',
        sparkline: {
          enabled: true,
        },
        animations: {
          enabled: false,
          animateGradually: {
            enabled: false,
          },
        },
      },
      series: [
        {
          data: this.data?.priceHistory?.baseCurrencyData?.data,
        },
      ],
      colors: [this.data?.chartColor],
      stroke: {
        curve: 'smooth',
        width: 2,
      },
      tooltip: {
        enabled: false,
      },
    };
  }
}
